import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Textarea } from "baseui/textarea";
import { Select, SIZE } from "baseui/select";
import { useSnackbar } from "baseui/snackbar";
import { Button, KIND, SIZE as BTN_SIZE } from "baseui/button";
import { FaCamera, FaCheckCircle, FaFileUpload } from "react-icons/fa";
import AuthContext from "../shared/AuthContext";
import { FormControl } from "baseui/form-control";
import { FlexGrid, FlexGridItem } from "baseui/flex-grid";
import { BlockProps } from "baseui/block";
import { format } from "date-fns";
import {
  appendBrainstormImagesToExtraInfo,
  IMAGE_SOURCE,
  sourceLabel,
  uploadQuestionImage,
} from "../../utils/extraInfoImages";
import "../styles/QuestionImageUpload.css";

const AddQuestionItem = forwardRef((props, ref) => {
  const { apiClient } = useContext(AuthContext);
  const { onAdd } = props;
  const [filterData, setFilterData] = useState({
    category: [],
    topic: [],
    subCategory1: [],
    subCategory2: [],
  });

  const [formData, setFormData] = useState({
    category: "",
    topic: "",
    subCategory1: "",
    subCategory2: "",
    question: "",
    answer: "",
    extraInfo: "",
    readStatus: false,
    addedOn: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
  });

  const [statusMessage, setStatusMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [imageAttachments, setImageAttachments] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const { enqueue } = useSnackbar();

  useImperativeHandle(ref, () => ({
    async submitForm() {
      return await handleSubmit();
    },
    getFormData() {
      return formData;
    },
  }));

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiClient.get("/api/get-filters");
        if (resp.status === 200) setFilterData(resp.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [apiClient]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const revokePreview = (previewUrl) => {
    if (previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const removeAttachment = (id) => {
    setImageAttachments((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) revokePreview(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleImageFiles = (fileList, source) => {
    const files = Array.from(fileList || []).filter(
      (f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|heic)$/i.test(f.name)
    );
    if (files.length === 0) {
      enqueue({ message: "Please choose a valid image file." });
      return;
    }
    const next = files.map((file) => ({
      id: `${source}-${file.name}-${file.lastModified}-${Math.random()}`,
      file,
      source,
      previewUrl: URL.createObjectURL(file),
      uploadedUrl: null,
    }));
    setImageAttachments((prev) => [...prev, ...next]);
  };

  const ensureImagesUploaded = async () => {
    if (imageAttachments.length === 0) return [];
    setUploadingImages(true);
    try {
      const uploaded = [];
      for (const item of imageAttachments) {
        if (item.uploadedUrl) {
          uploaded.push({ url: item.uploadedUrl, source: item.source });
          continue;
        }
        const url = await uploadQuestionImage(apiClient, item.file);
        uploaded.push({ url, source: item.source });
      }
      return uploaded;
    } finally {
      setUploadingImages(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    [
      "category",
      "topic",
      "subCategory1",
      "subCategory2",
      "question",
      "answer",
    ].forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validateForm()) return false;
    setStatusMessage("loading");

    try {
      const uploadedAttachments = await ensureImagesUploaded();
      const extraInfoWithImages = appendBrainstormImagesToExtraInfo(
        formData.extraInfo,
        uploadedAttachments
      );
      const payload = {
        ...formData,
        extraInfo: extraInfoWithImages,
        mediumUrl: uploadedAttachments[0]?.url || null,
      };

      await apiClient.post("/api/add-questions", payload);
      enqueue({
        message: "Question added successfully!",
        startEnhancer: () => (
          <FaCheckCircle size={16} style={{ marginRight: "8px" }} />
        ),
      });

      onAdd?.({ ...payload });

      imageAttachments.forEach((item) => revokePreview(item.previewUrl));
      setImageAttachments([]);

      setFormData({
        category: "",
        topic: "",
        subCategory1: "",
        subCategory2: "",
        question: "",
        answer: "",
        extraInfo: "",
        readStatus: false,
        addedOn: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
      });

      setErrors({});
      setStatusMessage("success");
      return true;
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "Failed to add the question. Please try again.";
      enqueue({ message: msg });
      setStatusMessage("error");
      return false;
    }
  };

  const itemProps: BlockProps = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
      <div className="admin-container">
        <FlexGrid
            flexGridColumnCount={[1, 1, 2, 2]}
            flexGridColumnGap="scale600"
            flexGridRowGap="scale600"
            style={{
              padding: "8px",
              marginBottom: "-58px",
            }}
        >
          {/* CATEGORY */}
          <FlexGridItem {...itemProps}>
            <FormControl label="">
              <>
                <Select
                    name="category"
                    value={formData.category ? [{ label: formData.category, id: formData.category }] : []}
                    onChange={({ value }) => setFormData((prev) => ({ ...prev, category: value[0]?.id }))}
                    options={filterData.category.map((c) => ({ label: c, id: c }))}
                    placeholder="Category/Notebook"
                    size={SIZE.default}
                    creatable
                    autoFocus={true}
                    error={!!errors.category}
                />
                {errors.category && <div className="error-message">{errors.category}</div>}
              </>
            </FormControl>
          </FlexGridItem>

          {/* TOPIC */}
          <FlexGridItem {...itemProps}>
            <FormControl label="">
              <>
                <Select
                    name="topic"
                    value={formData.topic ? [{ label: formData.topic, id: formData.topic }] : []}
                    onChange={({ value }) => setFormData((prev) => ({ ...prev, topic: value[0]?.id }))}
                    options={filterData.topic.map((t) => ({ label: t, id: t }))}
                    placeholder="Topic"
                    size={SIZE.default}
                    creatable
                    error={!!errors.topic}
                />
                {errors.topic && <div className="error-message">{errors.topic}</div>}
              </>
            </FormControl>
          </FlexGridItem>

          {/* SUBCATEGORY1 */}
          <FlexGridItem {...itemProps}>
            <FormControl label="">
              <>
                <Select
                    name="subCategory1"
                    value={formData.subCategory1 ? [{ label: formData.subCategory1, id: formData.subCategory1 }] : []}
                    onChange={({ value }) => setFormData((prev) => ({ ...prev, subCategory1: value[0]?.id }))}
                    options={filterData.subCategory1.map((s) => ({ label: s, id: s }))}
                    placeholder="Levels"
                    size={SIZE.default}
                    creatable
                    error={!!errors.subCategory1}
                />
                {errors.subCategory1 && <div className="error-message">{errors.subCategory1}</div>}
              </>
            </FormControl>
          </FlexGridItem>

          {/* SUBCATEGORY2 */}
          <FlexGridItem {...itemProps}>
            <FormControl label="">
              <>
                <Select
                    name="subCategory2"
                    value={formData.subCategory2 ? [{ label: formData.subCategory2, id: formData.subCategory2 }] : []}
                    onChange={({ value }) => setFormData((prev) => ({ ...prev, subCategory2: value[0]?.id }))}
                    options={filterData.subCategory2.map((s) => ({ label: s, id: s }))}
                    placeholder="Subtopic"
                    size={SIZE.default}
                    creatable
                    error={!!errors.subCategory2}
                />
                {errors.subCategory2 && <div className="error-message">{errors.subCategory2}</div>}
              </>
            </FormControl>
          </FlexGridItem>

          {/* QUESTION */}
          <FlexGridItem {...itemProps}>
            <FormControl label="">
              <>
                <Textarea
                    name="question"
                    value={formData.question}
                    onChange={handleChange}
                    placeholder="Enter Question"
                    rows={2}
                    size="compact"
                    clearable={true}
                    error={!!errors.question}
                />
                {errors.question && <div className="error-message">{errors.question}</div>}
              </>
            </FormControl>
          </FlexGridItem>

          {/* ANSWER */}
          <FlexGridItem {...itemProps}>
            <FormControl label="">
              <>
                <Textarea
                    name="answer"
                    value={formData.answer}
                    onChange={handleChange}
                    placeholder="Enter Answer"
                    rows={2}
                    size="compact"
                    clearable={true}
                    error={!!errors.answer}
                />
                {errors.answer && <div className="error-message">{errors.answer}</div>}
              </>
            </FormControl>
          </FlexGridItem>

          {/* MERGED EXTRA INFO FULL WIDTH */}
          <FlexGridItem
              overrides={{
                Block: {
                  style: {
                    flex: "1 0 100%", // span full width
                  },
                },
              }}
          >
            <FormControl label="">
              <>
                <Textarea
                    name="extraInfo"
                    value={formData.extraInfo}
                    onChange={handleChange}
                    placeholder="Enter Extra Info"
                    size="compact"
                    clearable
                    resize="vertical"
                    rows={3}
                    error={!!errors.extraInfo}
                    overrides={{
                      Root: {
                        style: {
                          width: "100%", // full width inside textarea
                        },
                      },
                    }}
                />
                {errors.extraInfo && (
                    <div className="error-message">{errors.extraInfo}</div>
                )}

                <div className="questionnaire-image-upload">
                  <p className="questionnaire-image-hint">
                    Add handwritten notes or brainstorming sheets. They appear in
                    Extra Info with a label showing whether you used the camera or
                    uploaded a file.
                  </p>
                  <div className="questionnaire-image-upload-actions">
                    <input
                      ref={galleryInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleImageFiles(e.target.files, IMAGE_SOURCE.FILE);
                        e.target.value = "";
                      }}
                    />
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleImageFiles(e.target.files, IMAGE_SOURCE.CAMERA);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      type="button"
                      kind={KIND.secondary}
                      size={BTN_SIZE.compact}
                      disabled={uploadingImages}
                      onClick={() => galleryInputRef.current?.click()}
                    >
                      <FaFileUpload style={{ marginRight: 6 }} />
                      Upload file
                    </Button>
                    <Button
                      type="button"
                      kind={KIND.secondary}
                      size={BTN_SIZE.compact}
                      disabled={uploadingImages}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <FaCamera style={{ marginRight: 6 }} />
                      Open camera
                    </Button>
                  </div>
                  {imageAttachments.length > 0 ? (
                    <div className="questionnaire-image-previews">
                      {imageAttachments.map((item) => (
                        <div
                          key={item.id}
                          className="questionnaire-image-preview"
                        >
                          <span
                            className={`questionnaire-image-source questionnaire-image-source--${item.source}`}
                          >
                            {sourceLabel(item.source)}
                          </span>
                          <img src={item.previewUrl} alt="Attachment preview" />
                          <Button
                            type="button"
                            kind={KIND.tertiary}
                            size={BTN_SIZE.mini}
                            className="questionnaire-image-remove"
                            onClick={() => removeAttachment(item.id)}
                            aria-label="Remove photo"
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </>
            </FormControl>
          </FlexGridItem>
        </FlexGrid>
      </div>
  );
});

export default AddQuestionItem;
